import BaseStep from 'shared/registration/components/steps/base-step';
import Label from 'shared/components/label';
import React, {Fragment, useCallback, useContext, useEffect, useMemo} from 'react';
import {useFormikContext} from 'formik';
import RadioButtons from 'shared/components/radio-buttons';
import FormikDropdown from 'src/components/formik-dropdown';
import AssetService from 'src/services/AssetService/AssetService';
import {CitizenshipOptions} from 'src/types/enums';
import {CitizenshipForm} from 'src/types/onboarding';
import {isDomestic} from 'src/utils/helpers';
import {ImpostorModeContext} from 'src/components/ImpostorModeContext';
import styles from './onboarding-citizenship.module.scss';
import {useLocation} from 'react-router-dom';

function getOptions(country: string) {
  const options = [
    {
      value: CitizenshipOptions.YES,
      label: 'Yes',
    },
    {
      value: CitizenshipOptions.NO,
      label: 'No',
    },
  ];

  // Decline to State option only for US-residing students.
  if (isDomestic(country)) {
    options.push({
      value: CitizenshipOptions.DECLINE,
      label: 'Decline to State',
    });
  }

  return options;
}

export const MANUALLY_SELECTED_DECLINE_TO_STATE = 'MANUALLY_SELECTED_DECLINE_TO_STATE';

export default function Citizenship() {
  const form = useFormikContext<CitizenshipForm>();
  const {isImpostor} = useContext(ImpostorModeContext);
  const {isUsCitizenOrResident, country, firstName} = form.values;
  const options = useMemo(() => getOptions(country), [country]);
  const {pathname} = useLocation();
  const onEligibilityPage = () => pathname.split('/').pop() === 'eligibility';

  useEffect(() => {
    if (form.values.isUsCitizenOrResident === CitizenshipOptions.DECLINE) {
      window.sessionStorage.setItem(MANUALLY_SELECTED_DECLINE_TO_STATE, 'true');
    }
  }, [form.values.isUsCitizenOrResident]);

  const loadCountryOptions = useCallback(
    () => AssetService.getCountryOptions({includeUs: false}),
    []
  );

  const subheader = (
    <>
      Some scholarships are available to U.S. students, and others to international students.
      <br />
      This information is only shared with the colleges {isImpostor
        ? `${firstName} is`
        : 'you are'}{' '}
      interested in.
    </>
  );

  const body = (
    <>
      <RadioButtons {...form.getFieldProps('isUsCitizenOrResident')} options={options} required />
      {isUsCitizenOrResident === CitizenshipOptions.NO && (
        <Label>
          What is your primary country of citizenship?
          <FormikDropdown cy="country" name="countryOfCitizenship" options={loadCountryOptions} />
        </Label>
      )}
      {isUsCitizenOrResident === CitizenshipOptions.DECLINE && (
        <div className={styles.decline}>
          <p className={styles.info}>
            <i className="rm rm-info-circle-r" aria-hidden="true" /> Just So You Know...
          </p>
          <p>
            Selecting &quot;Decline to State&quot; may impact {isImpostor ? "your child's" : 'your'}{' '}
            eligibility to earn micro-scholarships from some partner colleges.{' '}
            <a
              href="https://help.raise.me/hc/en-us/articles/360003767773-Who-can-see-my-Portfolio-and-other-personal-information-"
              rel="noopener noreferrer"
              aria-label="Personal information privacy (Opens in new tab)"
              target="_blank"
            >
              Read more on how citizen status is used at Raise Me
            </a>
            .
          </p>
        </div>
      )}
    </>
  );

  if (onEligibilityPage()) {
    return (
      <>
        <div className="shared-font body mb-16">{subheader}</div>
        {body}
      </>
    );
  } else {
    return (
      <BaseStep
        stepHeader={`${
          isImpostor ? `Is ${firstName}` : 'Are you'
        } a U.S. citizen or permanent resident?`}
        subHeader={subheader}
        className="citizenship-step"
      >
        {body}
      </BaseStep>
    );
  }
}
